import React, { useState, useRef } from 'react';
import { di } from 'react-magnetic-di';
// `DELAY_MS_SHOW` and `withOuterListeners` are not changed between `@ataslkit/profilecard` v13.x and v15.6.1
// so it's fine to use either `DELAY_MS_SHOW` and `withOuterListeners` from v13.x or v15.6.1
import { type ProfileCardAction, DELAY_MS_SHOW, withOuterListeners } from '@atlaskit/profilecard';
import { AnalyticsEventToProps } from '@atlassian/jira-product-analytics-bridge';
import { useCloudId } from '@atlassian/jira-tenant-context-controller/src/components/cloud-id/index.tsx';
import { useCallbackWithCondition } from '@atlassian/jira-feature-flagging-utils/src/utils/hooks/index.tsx';
import { fg } from '@atlassian/jira-feature-gating';
import { ProfileCardLegacyWrapper } from '../../common/ui/legacy-profile-card/index.tsx';
import {
	useHideProfileCardDelayed,
	useEventListener,
} from '../../common/ui/legacy-profile-card/utils.tsx';
import exposeApiWithBridge from '../../services/expose-api/index.tsx';
import type { UserDataFromMonolith } from '../../common/types.tsx';
import { useProfileClient } from '../../controllers/profileclient/index.tsx';
import { analyticsViewComponentName } from './constants.tsx';
import ProfileCardResourced from './profilecard-resourced/index.tsx';

const AnalyticsWrapper = AnalyticsEventToProps(analyticsViewComponentName, {
	onAnalytics: 'fired',
});
export const ProfileCardResourcedWithAnalytics = AnalyticsWrapper(
	withOuterListeners(ProfileCardResourced),
);

const eventListenerOptions = {
	passive: true,
} as const;

type Props = {
	onGetActions: (accountId: string, cleanup?: () => void) => ProfileCardAction[];
	onProfileClick?: (arg1: string) => void;
};

export const useOffset = () => useState<string | null>(null);
export const useUserData = () => useState<UserDataFromMonolith | null>(null);
export const useReferenceElement = () => useState<HTMLElement | null>(null);

export const ProfileCardLegacyView = ({ onGetActions, onProfileClick }: Props) => {
	// Required for mocking in VR Tests
	di(setTimeout);
	// Hooks
	const [userData, setUserData] = useUserData();
	const [referenceElement, setReferenceElement] = useReferenceElement();
	const [showProfileCard, setShowProfileCard] = useState<boolean>(false);
	const [offset, setOffset] = useOffset();
	const showTimeoutId = useRef<ReturnType<typeof setTimeout> | null>(null);
	const cloudId = useCloudId();
	const { profileClient } = useProfileClient();

	const onMouseLeaveCancelShowCard = useCallbackWithCondition(
		() => {
			if (showTimeoutId.current) {
				clearTimeout(showTimeoutId.current);
			}
		},
		[showTimeoutId],
		() => fg('jfp-magma-stability-platform-2'),
	);

	const onHideProfileCard = useCallbackWithCondition(
		() => {
			setShowProfileCard(false);
			onMouseLeaveCancelShowCard();
		},
		[setShowProfileCard, onMouseLeaveCancelShowCard],
		() => fg('jfp-magma-stability-platform-2'),
	);

	const { setTriggerRef, setCardRef, unsetTriggerRef, unsetCardRef, getTimeoutId } =
		useHideProfileCardDelayed(true, false, onHideProfileCard);

	// Add event listener using our hook
	useEventListener('mouseleave', unsetTriggerRef, referenceElement, eventListenerOptions);
	useEventListener(
		'mouseleave',
		onMouseLeaveCancelShowCard,
		referenceElement,
		eventListenerOptions,
	);
	useEventListener('mouseenter', setTriggerRef, referenceElement, eventListenerOptions);
	// @ts-expect-error - TS2345 - Argument of type 'Window & typeof globalThis' is not assignable to parameter of type 'HTMLElement | null'.
	useEventListener('scroll', onHideProfileCard, window, eventListenerOptions);

	const clearHideCardTimeout = (timeoutId: null | number) => {
		if (timeoutId) {
			clearTimeout(timeoutId);
		}
	};

	const setShowProfileCardDelayed = (): ReturnType<typeof setTimeout> =>
		setTimeout(() => setShowProfileCard(true), DELAY_MS_SHOW);

	const setData = (data: UserDataFromMonolith, ref: HTMLElement, offsetValue: string) => {
		clearHideCardTimeout(getTimeoutId());
		setOffset(offsetValue || '0, 8px');
		setUserData(data);
		setReferenceElement(ref);
		showTimeoutId.current = setShowProfileCardDelayed();
	};

	const api = { setData, hideProfileCard: onHideProfileCard };
	exposeApiWithBridge(api);

	return (
		showProfileCard &&
		referenceElement && (
			<ProfileCardLegacyWrapper
				onMouseEnter={setCardRef}
				onMouseLeave={unsetCardRef}
				referenceElement={referenceElement}
				offset={offset}
			>
				{() => (
					<ProfileCardResourcedWithAnalytics
						accountId={userData?.accountId}
						cloudId={cloudId}
						handleEscapeKeydown={onHideProfileCard}
						handleClickOutside={onHideProfileCard}
						profileClient={profileClient}
						actions={onGetActions(userData?.accountId ?? '', () => {
							clearHideCardTimeout(getTimeoutId());
							onHideProfileCard();
						})}
						onProfileClick={onProfileClick}
					/>
				)}
			</ProfileCardLegacyWrapper>
		)
	);
};
